import styled from 'styled-components'
import { tokens } from 'styling/tokens'

export const WizardShell = styled.div`
  color: ${tokens.text.primary};
  padding: 0 0 6rem;
`

export const WizardTopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.875rem;
  margin-bottom: 0.25rem;
`

export const BackChevron = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${tokens.text.primary};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  font-size: 1.25rem;
  line-height: 1;

  &:hover {
    filter: brightness(115%);
  }
`

export const WizardTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: ${tokens.text.primary};
  margin: 0;
`

export const WizardSubtitle = styled.div`
  font-size: 0.95rem;
  color: ${tokens.text.secondary};
  margin: 0.25rem 0 1.5rem 2.75rem;
`

/* --- Stepper: circles with connecting horizontal lines --- */

export const Stepper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  flex-wrap: nowrap;
  overflow-x: auto;
`

export const StepItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.625rem;
  white-space: nowrap;
`

export const StepCircle = styled.div<{ state: 'done' | 'active' | 'pending' }>`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  font-weight: 700;
  flex-shrink: 0;
  color: ${(props) =>
    props.state === 'active'
      ? tokens.accent.ink
      : props.state === 'done'
      ? tokens.accent.ink
      : tokens.text.secondary};
  background: ${(props) =>
    props.state === 'done'
      ? tokens.status.success.fg
      : props.state === 'active'
      ? tokens.accent.base
      : 'transparent'};
  border: 1px solid
    ${(props) =>
      props.state === 'done'
        ? tokens.status.success.fg
        : props.state === 'active'
        ? tokens.accent.base
        : tokens.border.hairline};
`

export const StepLabel = styled.div<{ state: 'done' | 'active' | 'pending' }>`
  font-size: 0.95rem;
  font-weight: 500;
  color: ${(props) =>
    props.state === 'active'
      ? tokens.accent.base
      : props.state === 'done'
      ? tokens.text.primary
      : tokens.text.secondary};
`

export const StepConnector = styled.div`
  flex: 1 1 40px;
  min-width: 24px;
  height: 1px;
  background: ${tokens.border.hairline};
`

/* Kept for backwards compatibility with older refs */
export const StepPill = styled.div<{ state: 'done' | 'active' | 'pending' }>`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
`

/* --- Footer --- */

export const WizardFooter = styled.div`
  ${(props) => props.theme.container};
  position: sticky;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.875rem 1.25rem;
  margin-top: 1.25rem;
`

export const FooterStep = styled.div`
  font-size: 0.95rem;
  color: ${tokens.text.secondary};
  text-align: center;
  flex: 1;
`

export const FooterLeft = styled.div`
  display: flex;
  justify-content: flex-start;
  min-width: 160px;
`

export const FooterRight = styled.div`
  display: flex;
  justify-content: flex-end;
  min-width: 160px;
`

export const BackTextButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${tokens.text.primary};
  font-size: 0.95rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    filter: brightness(120%);
  }
`

/* ------------- Step 1 ------------- */

export const ModePickerRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
`

export const ModeCard = styled.button<{ active?: boolean }>`
  ${(props) => props.theme.container};
  text-align: left;
  padding: 1rem 1.1rem;
  border: 1px solid
    ${(props) => (props.active ? tokens.accent.base : 'transparent')};
  cursor: pointer;
  color: ${tokens.text.primary};
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  transition: 0.15s ease;

  &:hover {
    border-color: ${tokens.accent.base};
  }
`

export const ModeCardIcon = styled.div<{ active?: boolean }>`
  width: 34px;
  height: 34px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${(props) =>
    props.active ? `${tokens.accent.base}1f` : tokens.surface.inset};
  color: ${(props) =>
    props.active ? tokens.accent.base : tokens.text.secondary};
  margin-bottom: 0.375rem;

  svg {
    width: 18px;
    height: 18px;
  }
`

export const ModeCardTitle = styled.div<{ active?: boolean }>`
  font-size: 1rem;
  font-weight: 700;
  color: ${(props) => (props.active ? tokens.accent.base : tokens.text.primary)};
`

export const ModeCardSubtitle = styled.div`
  font-size: 0.85rem;
  color: ${tokens.text.secondary};
`

export const FiltersBar = styled.div`
  display: grid;
  grid-template-columns: minmax(260px, 2fr) repeat(3, minmax(0, 1fr)) minmax(
      0,
      1fr
    ) 40px;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  align-items: center;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr 1fr;
  }
`

export const SimpleFiltersBar = styled.div`
  display: grid;
  grid-template-columns: minmax(260px, 2fr) repeat(3, minmax(0, 1fr)) 40px;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  align-items: center;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr 1fr;
  }
`

export const EmployeeListWrapper = styled.div`
  ${(props) => props.theme.container};
  padding: 1rem 1.25rem;
  max-height: 520px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

export const EmployeeListHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.125rem 0 0.75rem;
  font-size: 0.75rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${tokens.text.secondary};
  font-weight: 600;
  border-bottom: 1px solid ${tokens.border.hairline};
  margin-bottom: 0.75rem;

  > span:first-of-type {
    flex: 1;
  }
`

export const SelectAllButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${tokens.accent.base};
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: normal;
  text-transform: none;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;

  &:hover {
    background: ${tokens.accent.base}14;
  }
`

export const EmployeeRow = styled.div<{ selected?: boolean }>`
  display: grid;
  grid-template-columns: 28px 2fr 1fr 1.2fr 1.4fr auto;
  gap: 1rem;
  padding: 0.75rem 1rem;
  align-items: center;
  border-radius: 12px;
  border: 1px solid
    ${(props) => (props.selected ? tokens.accent.base : tokens.border.hairline)};
  background: ${(props) =>
    props.selected ? `${tokens.accent.base}0d` : 'transparent'};
  cursor: pointer;
  transition: 0.15s ease;

  &:hover {
    border-color: ${tokens.accent.base};
  }
`

export const Radio = styled.div<{ checked?: boolean; filled?: boolean }>`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid
    ${(props) =>
      props.checked || props.filled ? tokens.accent.base : tokens.text.secondary};
  background: ${(props) => (props.filled ? tokens.accent.base : 'transparent')};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &::after {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${(props) =>
      props.checked && !props.filled ? tokens.accent.base : 'transparent'};
  }
`

/* Backwards compat — Checkbox alias (still used internally by the step component). */
export const Checkbox = styled.div<{ checked?: boolean }>`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid
    ${(props) => (props.checked ? tokens.accent.base : tokens.text.secondary)};
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &::after {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${(props) => (props.checked ? tokens.accent.base : 'transparent')};
  }
`

export const TeamChip = styled.span`
  padding: 3px 12px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 500;
  border: 1px solid ${tokens.border.hairline};
  color: ${tokens.text.secondary};
  background: ${tokens.surface.inset};
  white-space: nowrap;
  width: fit-content;
`

export const StatusChip = styled.span<{ tone?: 'onsite' | 'offsite' }>`
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 500;
  border: 1px solid
    ${(props) => (props.tone === 'offsite' ? tokens.text.secondary : tokens.status.success.fg)};
  color: ${(props) => (props.tone === 'offsite' ? tokens.text.secondary : tokens.status.success.fg)};
  background: ${(props) =>
    props.tone === 'offsite' ? 'transparent' : `${tokens.status.success.fg}14`};
`

export const SelectionBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0 0 0.5rem;
  font-size: 0.75rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${tokens.text.secondary};
  font-weight: 600;

  span[data-clickable='true'] {
    cursor: pointer;
    color: ${tokens.accent.base};
    margin-left: auto;
    letter-spacing: normal;
    text-transform: none;
    font-weight: 500;
  }
`

/* ------------- Step 2 ------------- */

export const SectionCard = styled.div`
  ${(props) => props.theme.container};
  padding: 1rem 1.25rem;
  margin-bottom: 1rem;
`

export const SectionTitle = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: ${tokens.text.primary};
  margin-bottom: 0.25rem;
`

export const SectionSub = styled.div`
  font-size: 0.8rem;
  color: ${tokens.text.secondary};
  margin-bottom: 0.75rem;
`

export const MetaChipsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
  margin-top: 0.75rem;
`

export const MetaChip = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.75rem;
  border: 1px solid ${tokens.border.hairline};
  border-radius: ${tokens.radius.chip};
  font-size: 0.875rem;
  color: ${tokens.text.primary};
`

export const MetaChipLabel = styled.span`
  color: ${tokens.text.secondary};
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-right: 0.25rem;
`

export const FieldError = styled.div`
  color: ${tokens.status.danger.fg};
  font-size: 0.75rem;
  margin-top: 0.35rem;
`

export const RuleBanner = styled.div`
  ${(props) => props.theme.container};
  border: 1px solid ${tokens.accent.base};
  background: ${tokens.accent.base}14;
  padding: 0.75rem 1rem;
  color: ${tokens.accent.base};
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
  font-size: 0.875rem;

  strong {
    font-weight: 700;
    display: block;
    margin-bottom: 0.125rem;
  }
`

export const DangerBanner = styled(RuleBanner)`
  border-color: ${tokens.status.danger.fg};
  background: ${tokens.status.danger.fg}14;
  color: ${tokens.status.danger.fg};
`

/* ------------- Step 3 ------------- */

export const KpiRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
`

export const KpiTile = styled.div<{ accentColor: string }>`
  ${(props) => props.theme.container};
  padding: 1rem 1.25rem;
  border-left: 3px solid ${(props) => props.accentColor};
`

export const KpiValue = styled.div<{ accentColor: string }>`
  font-size: 1.75rem;
  font-weight: 700;
  color: ${(props) => props.accentColor};
  line-height: 1;
`

export const KpiLabel = styled.div`
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${tokens.text.secondary};
  font-weight: 600;
  margin-top: 0.5rem;
`

export const Panel = styled.div<{ accentColor?: string }>`
  ${(props) => props.theme.container};
  padding: 1rem 1.25rem;
  margin-bottom: 0.75rem;
  border-left: 3px solid ${(props) => props.accentColor ?? tokens.border.hairline};
`

export const PanelTitle = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: ${tokens.text.primary};
  margin-bottom: 0.125rem;
`

export const PanelSub = styled.div`
  font-size: 0.75rem;
  color: ${tokens.text.secondary};
  margin-bottom: 0.5rem;
`

export const PanelTableHeader = styled.div<{ cols: string }>`
  display: grid;
  grid-template-columns: ${(p) => p.cols};
  gap: 0.75rem;
  padding: 0.5rem 0;
  font-size: 0.7rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${tokens.text.secondary};
  font-weight: 600;
  border-bottom: 1px solid ${tokens.border.hairline};
`

export const PanelTableRow = styled.div<{ cols: string }>`
  display: grid;
  grid-template-columns: ${(p) => p.cols};
  gap: 0.75rem;
  padding: 0.625rem 0;
  align-items: center;
  font-size: 0.875rem;
  border-bottom: 1px solid ${tokens.border.hairline};

  &:last-child {
    border-bottom: none;
  }
`

export const ReasonText = styled.span`
  color: ${tokens.status.danger.fg};
  font-size: 0.875rem;
`

export const Arrow = styled.span`
  color: ${tokens.accent.base};
  margin: 0 0.25rem;
`

/* ------------- Step 4 ------------- */

export const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
`

export const SummaryChip = styled.div`
  padding: 0.75rem 1rem;
  border-radius: ${tokens.radius.chip};
  border: 1px solid ${tokens.border.hairline};
  color: ${tokens.text.primary};
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

export const SummaryChipLabel = styled.div`
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${tokens.text.secondary};
  font-weight: 600;
`

export const SummaryChipValue = styled.div`
  font-size: 0.95rem;
  font-weight: 600;
`

/* ------------- Step 5 ------------- */

export const ResultsHero = styled.div`
  ${(props) => props.theme.container};
  padding: 1.25rem;
  text-align: center;
  margin-bottom: 1rem;
`

export const ResultsIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${tokens.status.success.fg}30;
  color: ${tokens.status.success.fg};
  margin: 0 auto 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
`

export const ResultsColumns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-bottom: 1rem;
`

export const ResultList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`

export const ResultRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.25rem 0;
  font-size: 0.875rem;
  color: ${tokens.text.primary};
  border-bottom: 1px dashed ${tokens.border.hairline};

  &:last-child {
    border-bottom: none;
  }
`

export const FinalSummary = styled.div`
  ${(props) => props.theme.container};
  padding: 1rem 1.25rem;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
`
